import { X, CheckCircle, HelpCircle } from "lucide-react";

const ViewModal = ({ service, onClose, onClick }) => {
  if (!service) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-[#0bb6d9] to-[#3cd7f6] text-white p-6 flex justify-between items-center rounded-t-2xl z-10">
          <h2 className="text-2xl font-bold">Service Details</h2>
          <button
            onClick={onClose}
            className="hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 uppercase tracking-wide">
                Main Image
              </label>
              <div className="rounded-lg overflow-hidden shadow-md h-64">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 uppercase tracking-wide">
                Sub Image
              </label>
              <div className="rounded-lg overflow-hidden shadow-md h-64">
                <img
                  src={service.subImage}
                  alt={`${service.title} - Sub`}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-6 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Title
              </label>
              <h3 className="text-2xl font-bold text-gray-800">
                {service.title}
              </h3>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Description
              </label>
              <p className="text-gray-600 leading-relaxed">
                {service.description}
              </p>
            </div>
          </div>

          <div className="bg-blue-50 rounded-lg p-6 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Sub Title
              </label>
              <h4 className="text-xl font-semibold text-gray-800">
                {service.subTitle}
              </h4>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Sub Descriptions
              </label>
              <div className="space-y-3">
                {service.subDescription && service.subDescription.length > 0 ? (
                  service.subDescription.map((desc, index) => (
                    <div
                      key={index}
                      className="flex items-start bg-white rounded-lg p-3"
                    >
                      <CheckCircle className="w-5 h-5 text-[#0bb6d9] mr-3 flex-shrink-0 mt-0.5" />
                      <p className="text-gray-700">{desc}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 italic">
                    No sub descriptions available
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="bg-purple-50 rounded-lg p-6 space-y-4 h-80 overflow-y-auto">
            <label className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
              <CheckCircle className="w-5 h-5 text-purple-600 mr-2" />
              Features
            </label>

            <div className="space-y-4">
              {service.features && service.features.length > 0 ? (
                service.features.map((feature, index) => (
                  <div
                    key={index}
                    className="flex items-start flex-col bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition"
                  >
                    <div className="flex gap-2 items-center">
                      <img
                        src={feature.image}
                        alt={feature.title}
                        className="w-10 h-10 rounded-full object-cover mr-4 border border-gray-200"
                      />
                      <h3 className="text-gray-800 font-semibold text-base">
                        {feature.title}
                      </h3>
                    </div>
                    <div>
                      <p className="text-gray-600 text-sm mt-1">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 italic">
                  No features listed
                </p>
              )}
            </div>
          </div>

          <div className="bg-yellow-50 rounded-lg p-6 space-y-4">
            <label className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
              <HelpCircle className="w-6 h-6 text-yellow-600 mr-2" />
              Frequently Asked Questions
            </label>
            <div className="space-y-4">
              {service.faqs && service.faqs.length > 0 ? (
                service.faqs.map((faq, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-lg p-4 border-l-4 border-yellow-500"
                  >
                    <p className="font-semibold text-gray-800 mb-2">
                      <span className="text-yellow-600">Q{index + 1}:</span>{" "}
                      {faq.question || faq.q || faq}
                    </p>
                    {(faq.answer || faq.a) && (
                      <p className="text-gray-600 text-sm pl-6">
                        <span className="font-semibold text-gray-700">A:</span>{" "}
                        {faq.answer || faq.a}
                      </p>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-gray-500 italic">No FAQs available</p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-x-5 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-8 py-3 bg-gray-600 text-white rounded-lg font-semibold hover:bg-gray-700 transition-colors duration-300 shadow-md hover:shadow-lg"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewModal;